import { useEffect, useMemo, useState } from 'react';
import type {
  CreateQuestionnaireQuestionInput,
  QuestionnaireDataLink,
  QuestionnaireEventField,
  QuestionnaireQuestion,
  QuestionnaireQuestionType,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useEventTags } from '../../hooks/useEvents';
import { useTimeline, useTimelines } from '../../hooks/useEventOps';
import { useEventTypeDefaultTags } from '../../hooks/useSettings';
import { useVendorCategories } from '../../hooks/useVendors';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-1';

export const QUESTION_TYPE_LABELS: Record<QuestionnaireQuestionType, string> = {
  TEXT: 'Short text',
  LONG_TEXT: 'Long text',
  NUMBER: 'Number',
  YES_NO: 'Yes / No',
  SINGLE_CHOICE: 'Single choice',
  TIME: 'Time',
  DATE: 'Date',
  ADDRESS: 'Address',
  VENDOR: 'Vendor (pick or type)',
  CONTACT: 'Contact (name + phone)',
};

type DataLinkKind = '' | QuestionnaireDataLink['kind'];

export interface QuestionEditModalProps {
  open: boolean;
  onClose: () => void;
  question: QuestionnaireQuestion | null;
  /** Event the questionnaire belongs to — enables timeline entry linking + tag suggestions. */
  eventId?: string | null;
  /** Event type of the questionnaire — tag suggestions come from its default tags. */
  eventTypeId?: string | null;
  /** Linked timeline template — timeline links pick their section/entry from it. */
  timelineTemplateId?: string | null;
  onSubmit: (input: CreateQuestionnaireQuestionInput & { timelineEntryId?: string | null }) => void;
  submitting?: boolean;
}

/**
 * Create/edit a questionnaire question, including its data link: which event
 * data it prefills from and can be applied back to.
 */
export function QuestionEditModal({
  open,
  onClose,
  question,
  eventId,
  eventTypeId,
  timelineTemplateId,
  onSubmit,
  submitting,
}: QuestionEditModalProps) {
  const [prompt, setPrompt] = useState('');
  const [helpText, setHelpText] = useState('');
  const [questionType, setQuestionType] = useState<QuestionnaireQuestionType>('TEXT');
  const [isRequired, setIsRequired] = useState(false);
  const [choicesText, setChoicesText] = useState('');
  const [allowOther, setAllowOther] = useState(false);
  const [linkKind, setLinkKind] = useState<DataLinkKind>('');
  const [eventField, setEventField] = useState<QuestionnaireEventField>('startAt');
  const [tagCode, setTagCode] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [entryTitleMatch, setEntryTitleMatch] = useState('');
  const [timelineEntryId, setTimelineEntryId] = useState('');
  const [vendorCategory, setVendorCategory] = useState('');

  const { data: eventTags = [] } = useEventTags(eventId ?? undefined);
  const { data: defaultTags = [] } = useEventTypeDefaultTags(eventTypeId ?? undefined);
  const { data: timelines = [] } = useTimelines(eventId ?? undefined);
  const { data: timelineTemplate } = useTimeline(timelineTemplateId ?? '');
  const { data: vendorCategories = [] } = useVendorCategories();

  const timelineEntries = useMemo(
    () =>
      eventId
        ? timelines
            .filter((t) => !t.isTemplate)
            .flatMap((t) =>
              t.sections.flatMap((s) =>
                s.entries.map((e) => ({
                  ...e,
                  sectionName: s.name,
                  fromLinkedTemplate:
                    !!timelineTemplateId && t.sourceTemplateId === timelineTemplateId,
                })),
              ),
            )
        : [],
    [eventId, timelines, timelineTemplateId],
  );

  // Section (header) → entry (line) options come from the linked timeline template.
  const templateSections = timelineTemplate?.sections ?? [];
  const selectedTemplateSection =
    templateSections.find((s) => s.name.toLowerCase() === sectionName.toLowerCase()) ?? null;

  // Tag suggestions: the event type's default tags + the event's own tags.
  const tagOptions = useMemo(() => {
    const byCode = new Map<string, string>();
    for (const tag of defaultTags) byCode.set(tag.code, tag.description);
    for (const tag of eventTags) if (!byCode.has(tag.code)) byCode.set(tag.code, tag.description);
    return [...byCode.entries()].map(([code, description]) => ({ code, description }));
  }, [defaultTags, eventTags]);

  useEffect(() => {
    if (!open) return;
    setPrompt(question?.prompt ?? '');
    setHelpText(question?.helpText ?? '');
    setQuestionType(question?.questionType ?? 'TEXT');
    setIsRequired(question?.isRequired ?? false);
    setChoicesText((question?.config.choices ?? []).join('\n'));
    setAllowOther(question?.config.allowOther ?? false);
    const dataLink = question?.dataLink ?? null;
    setLinkKind(dataLink?.kind ?? '');
    setEventField(dataLink?.kind === 'EVENT_FIELD' ? dataLink.field : 'startAt');
    setTagCode(dataLink?.kind === 'EVENT_TAG' ? dataLink.tagCode : '');
    setSectionName(dataLink?.kind === 'TIMELINE_TIME' ? (dataLink.sectionName ?? '') : '');
    setEntryTitleMatch(dataLink?.kind === 'TIMELINE_TIME' ? dataLink.entryTitleMatch : '');
    setTimelineEntryId(question?.timelineEntryId ?? '');
    setVendorCategory(dataLink?.kind === 'VENDOR_LINK' ? (dataLink.vendorCategory ?? '') : '');
  }, [open, question]);

  /**
   * Best event timeline entry for a section/title picked from the template —
   * same tiering as the backend resolver, including the legacy contains match.
   */
  function resolveEventEntryId(section: string, title: string): string {
    const norm = (value: string) => value.trim().toLowerCase();
    const tiers = [
      timelineEntries.filter(
        (e) =>
          e.fromLinkedTemplate &&
          norm(e.sectionName) === norm(section) &&
          norm(e.title) === norm(title),
      ),
      timelineEntries.filter(
        (e) => norm(e.sectionName) === norm(section) && norm(e.title) === norm(title),
      ),
      timelineEntries.filter((e) => norm(e.title) === norm(title)),
      timelineEntries.filter((e) => norm(e.title).includes(norm(title))),
    ];
    for (const tier of tiers) {
      if (tier.length > 0) return tier[0].id;
    }
    return '';
  }

  function buildDataLink(): QuestionnaireDataLink | null {
    switch (linkKind) {
      case 'EVENT_FIELD':
        return { kind: 'EVENT_FIELD', field: eventField };
      case 'EVENT_TAG':
        return tagCode.trim() ? { kind: 'EVENT_TAG', tagCode: tagCode.trim() } : null;
      case 'TIMELINE_TIME':
        return entryTitleMatch.trim()
          ? {
              kind: 'TIMELINE_TIME',
              entryTitleMatch: entryTitleMatch.trim(),
              sectionName: sectionName.trim() || undefined,
            }
          : null;
      case 'VENDOR_LINK':
        return { kind: 'VENDOR_LINK', vendorCategory: vendorCategory || undefined };
      default:
        return null;
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) return;
    const choices = choicesText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    onSubmit({
      prompt: prompt.trim(),
      helpText: helpText.trim() || undefined,
      questionType,
      isRequired,
      config: {
        ...(questionType === 'SINGLE_CHOICE' ? { choices, allowOther } : {}),
      },
      dataLink: buildDataLink(),
      timelineEntryId:
        linkKind === 'TIMELINE_TIME' && eventId ? timelineEntryId || null : undefined,
    });
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={question ? 'Edit Question' : 'Add Question'}
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="question-edit-form"
            disabled={submitting || !prompt.trim()}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="question-submit"
          >
            {submitting ? 'Saving…' : 'Save Question'}
          </button>
        </div>
      }
    >
      <form id="question-edit-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="question-prompt" className={LABEL_CLASS}>
            Question
          </label>
          <textarea
            id="question-prompt"
            rows={2}
            className={INPUT_CLASS}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="question-help" className={LABEL_CLASS}>
            Help text (optional)
          </label>
          <input
            id="question-help"
            type="text"
            className={INPUT_CLASS}
            value={helpText}
            onChange={(e) => setHelpText(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="question-type" className={LABEL_CLASS}>
              Answer type
            </label>
            <select
              id="question-type"
              className={INPUT_CLASS}
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value as QuestionnaireQuestionType)}
            >
              {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 sm:mt-7">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
            Required
          </label>
        </div>

        {questionType === 'SINGLE_CHOICE' && (
          <div className="space-y-2">
            <div>
              <label htmlFor="question-choices" className={LABEL_CLASS}>
                Choices (one per line)
              </label>
              <textarea
                id="question-choices"
                rows={3}
                className={INPUT_CLASS}
                value={choicesText}
                onChange={(e) => setChoicesText(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={allowOther}
                onChange={(e) => setAllowOther(e.target.checked)}
              />
              Allow a free-text &ldquo;Other&rdquo; answer
            </label>
          </div>
        )}

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <div>
            <label htmlFor="question-link-kind" className={LABEL_CLASS}>
              Link to event data (prefill + apply back)
            </label>
            <select
              id="question-link-kind"
              className={INPUT_CLASS}
              value={linkKind}
              onChange={(e) => setLinkKind(e.target.value as DataLinkKind)}
              data-testid="question-link-kind"
            >
              <option value="">Not linked</option>
              <option value="EVENT_FIELD">Event field (date, venue, guest count)</option>
              <option value="EVENT_TAG">Event tag value (bride name, addresses…)</option>
              <option value="TIMELINE_TIME">Timeline entry time</option>
              <option value="VENDOR_LINK">Event vendor list</option>
            </select>
          </div>

          {linkKind === 'EVENT_FIELD' && (
            <div>
              <label htmlFor="question-event-field" className={LABEL_CLASS}>
                Event field
              </label>
              <select
                id="question-event-field"
                className={INPUT_CLASS}
                value={eventField}
                onChange={(e) => setEventField(e.target.value as QuestionnaireEventField)}
              >
                <option value="startAt">Event date</option>
                <option value="venue">Venue (prefill only)</option>
                <option value="guestCount">Guest count</option>
              </select>
            </div>
          )}

          {linkKind === 'EVENT_TAG' && (
            <div>
              <label htmlFor="question-tag-code" className={LABEL_CLASS}>
                Tag code
              </label>
              <input
                id="question-tag-code"
                type="text"
                className={INPUT_CLASS}
                value={tagCode}
                onChange={(e) => setTagCode(e.target.value)}
                list="questionnaire-event-tag-codes"
                placeholder="e.g. bride_name"
              />
              <datalist id="questionnaire-event-tag-codes">
                {tagOptions.map((tag) => (
                  <option key={tag.code} value={tag.code}>
                    {tag.description}
                  </option>
                ))}
              </datalist>
              <p className="mt-1 text-xs text-gray-500">
                The answer prefills from — and applies back to — this event tag. Suggestions include
                the event type&apos;s tags; a missing tag is created on the event when the
                questionnaire is used.
              </p>
            </div>
          )}

          {linkKind === 'TIMELINE_TIME' && (
            <div className="space-y-2">
              {timelineTemplate ? (
                <>
                  <div data-testid="question-timeline-section">
                    <label className={LABEL_CLASS}>Timeline section (header)</label>
                    <SearchableListSelect
                      items={templateSections}
                      value={selectedTemplateSection?.name ?? null}
                      onChange={(value) => {
                        setSectionName(typeof value === 'string' ? value : '');
                        setEntryTitleMatch('');
                        setTimelineEntryId('');
                      }}
                      getValue={(section) => section.name}
                      getLabel={(section) => section.name}
                      placeholder="Any section"
                      ariaLabel="Timeline section"
                      emptyMessage="The linked timeline template has no sections yet."
                    />
                  </div>
                  <div data-testid="question-timeline-template-entry">
                    <label className={LABEL_CLASS}>Timeline entry (line)</label>
                    <SearchableListSelect
                      items={
                        selectedTemplateSection
                          ? selectedTemplateSection.entries
                          : templateSections.flatMap((section) => section.entries)
                      }
                      value={entryTitleMatch || null}
                      onChange={(value) => {
                        const title = typeof value === 'string' ? value : '';
                        setEntryTitleMatch(title);
                        if (eventId && title) {
                          setTimelineEntryId(resolveEventEntryId(sectionName, title));
                        }
                      }}
                      getValue={(entry) => entry.title}
                      getLabel={(entry) => [entry.time, entry.title].filter(Boolean).join(' — ')}
                      placeholder="Pick an entry…"
                      ariaLabel="Timeline entry"
                      emptyMessage="No entries in the linked timeline template."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Both pickers come from the linked timeline template. When the questionnaire is
                      used on an event without a timeline from that template, the timeline is cloned
                      automatically.
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <label htmlFor="question-entry-match" className={LABEL_CLASS}>
                    Timeline entry title match
                  </label>
                  <input
                    id="question-entry-match"
                    type="text"
                    className={INPUT_CLASS}
                    value={entryTitleMatch}
                    onChange={(e) => setEntryTitleMatch(e.target.value)}
                    placeholder="e.g. Chuppah"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used to auto-link the matching timeline entry when the template is attached to
                    an event. Link a timeline template to the questionnaire to pick the section and
                    entry from a list.
                  </p>
                </div>
              )}
              {eventId && (
                <div data-testid="question-timeline-entry">
                  <label className={LABEL_CLASS}>Linked timeline entry</label>
                  <SearchableListSelect
                    items={timelineEntries}
                    value={timelineEntryId || null}
                    onChange={(value) => setTimelineEntryId(typeof value === 'string' ? value : '')}
                    getValue={(entry) => entry.id}
                    getLabel={(entry) => [entry.time, entry.title].filter(Boolean).join(' — ')}
                    getSubtitle={(entry) => entry.sectionName}
                    placeholder="Not linked"
                    ariaLabel="Linked timeline entry"
                    emptyMessage="This event has no timeline entries yet."
                  />
                </div>
              )}
            </div>
          )}

          {linkKind === 'VENDOR_LINK' && (
            <div data-testid="question-vendor-category">
              <label className={LABEL_CLASS}>Vendor category (optional)</label>
              <SearchableListSelect
                items={vendorCategories}
                value={vendorCategory || null}
                onChange={(value) => setVendorCategory(typeof value === 'string' ? value : '')}
                getValue={(category) => category.name}
                getLabel={(category) => category.name}
                placeholder="Any category"
                ariaLabel="Vendor category"
              />
              <p className="mt-1 text-xs text-gray-500">
                Clients can pick from your vendor list or type a name — they can never create a
                vendor. On review, a linked vendor can be added to the event in one click.
              </p>
            </div>
          )}
        </div>
      </form>
    </EntityModal>
  );
}
